import {getMockDb, updateMockDb, MockDb} from './mockDb';
import {getCurrentPersonaId} from './fixtures';
import {getLogicalDay} from '../../../ui/screens/Lists/components/utils';
import {Item} from '../../../API';
import {reliabilityAuditResolvers, newIssue} from './resolversReliabilityAudit';

type Resolver = (variables: any) => any;

const listResult = (items: unknown[]) => ({items, nextToken: null, startedAt: Date.now()});

// These operations are expected to return empty results in mock mode — no warning needed.
const SILENT_EMPTY_LISTS = new Set([
    'listQuizzes',
    'listQuizAnswers',
    'listQuizStats',
    'listAckOverviewTable',
    'listAccessRoles',
    'listUserRoles',
    'listRolePermissions',
    'listMemberships',
    'listPayments',
    'listRoles',
    'listRolesForUser',
]);

// Generic update mutations: field name → collection key in MockDb.
const MUTABLE_COLLECTIONS: Record<string, keyof MockDb> = {
    updateClientUser: 'clientUsers',
    updateWorkspace: 'workspaces',
    updateClient: 'client',
    updateItem: 'items',
    deleteItem: 'items',            // soft delete — caller sends full item with trashed:true
    updateTaskListUnit: 'taskListUnits',
    updateTask: 'tasks',            // persists schedule/name/config edits (union: ConflictError | Task)
    updateTaskReport: 'taskReports',// persists report status/comment/image edits
};

const resolvers: Record<string, Resolver> = {
    ...reliabilityAuditResolvers,
    listClientUsers: () => listResult(getMockDb().clientUsers),
    listWorkspaces: () => listResult(getMockDb().workspaces),
    notifByUser: () => listResult([]),
    // wsUserByUserId feeds UserWorkspacesDataSource — must filter by userId so non-owner personas see their workspaces.
    wsUserByUserId: (variables: any) => {
        const userId = variables?.userId?.eq;
        return listResult(getMockDb().wsUsers.filter(u => !userId || u.userId === userId));
    },
    // wsUserByWsId feeds the Members list and acknowledgment confirmed/unconfirmed display.
    wsUserByWsId: () => listResult(getMockDb().wsUsers),
    getClient: () => getMockDb().client,
    clientUsage: () => ({
        clientId: getMockDb().client.id,
        itemsCount: getMockDb().items.length,
        quizzesCount: 0,
        aiUsageCount: 0,
    }),

    listItems: (variables: any) => {
        const wsId = variables?.id;
        const items = getMockDb().items.filter(item =>
            !wsId || item.workspaceId === wsId
        );
        return listResult(items);
    },

    listTaskListUnits: (variables: any) => {
        const wsId = variables?.workspaceId;
        const units = getMockDb().taskListUnits.filter(u =>
            !wsId || u.workspaceId === wsId
        );
        return listResult(units);
    },

    listTasksForUnit: (variables: any) => {
        const unitId = variables?.taskListUnitId;
        const tasks = getMockDb().tasks.filter(t =>
            !unitId || t.taskListUnitId === unitId
        );
        return listResult(tasks);
    },

    hasReports: (variables: any) => {
        const taskId = variables?.taskId;
        return getMockDb().taskReports.some(r => r.taskId === taskId);
    },

    listTasksReportsForUnit: (variables: any) => {
        const unitId = variables?.taskListUnitId;
        const reports = getMockDb().taskReports.filter(r =>
            !unitId || r.taskListUnitId === unitId
        );
        return listResult(reports);
    },

    createItem: (variables: any) => {
        const input = variables?.input;
        if (!input) return null;
        const now = new Date().toISOString();
        const item: Item = {
            __typename: 'Item',
            id: crypto.randomUUID(),
            ownerId: input.ownerId ?? getMockDb().client.id,
            workspaceId: input.workspaceId,
            name: input.name ?? '',
            type: input.type ?? 'LEAF',
            childIds: input.childIds ?? '[]',
            parentIds: input.parentIds ?? '[]',
            readRoles: input.readRoles ?? '[]',
            writeRoles: input.writeRoles ?? '[]',
            creator: input.creator ?? '',
            archived: input.archived ?? false,
            trashed: input.trashed ?? false,
            pinned: input.pinned ?? false,
            createdAt: now,
            updatedAt: now,
            version: 1,
            deletedAt: null,
            slug: null,
            description: null,
            acknowledgement: null,
            acknowledgementRequired: null,
            ackUpdatedBy: null,
            statusRestoreParentIds: null,
            statusSourceLibraryId: null,
            statusUpdatedBy: null,
            updatedBy: input.creator ?? '',
            pinnedIds: '[]',
        };
        updateMockDb(db => {
            db.items.push(item);

            // If this item has a parent item (not workspace), update parent's childIds.
            const parentIds: string[] = JSON.parse(item.parentIds || '[]');
            for (const parentId of parentIds) {
                const parentIdx = db.items.findIndex(i => i.id === parentId);
                if (parentIdx !== -1) {
                    const parentChildIds: string[] = JSON.parse(db.items[parentIdx].childIds || '[]');
                    if (!parentChildIds.includes(item.id)) {
                        parentChildIds.push(item.id);
                        db.items[parentIdx] = {...db.items[parentIdx], childIds: JSON.stringify(parentChildIds)};
                    }
                }
            }
        });
        return item;
    },

    createWorkspace: (variables: any) => {
        const input = variables?.input;
        if (!input) return null;
        const now = new Date().toISOString();
        const workspace = {
            __typename: 'Workspace' as const,
            id: crypto.randomUUID(),
            ownerId: input.ownerId ?? getMockDb().client.id,
            name: input.name ?? 'New Workspace',
            slug: input.slug ?? `ws-${Date.now()}`,
            archivedIds: '[]',
            avatarPath: null,
            childIds: '[]',
            deletedAt: null,
            pinnedIds: '[]',
            trashedIds: '[]',
            createdAt: now,
            updatedAt: now,
            version: 1,
        };
        updateMockDb(db => { db.workspaces.push(workspace); });
        return workspace;
    },

    createTask: (variables: any) => {
        const input = variables?.input;
        if (!input) return null;
        const now = new Date().toISOString();
        const me = getCurrentPersonaId();
        const task = {
            __typename: 'Task' as const,
            id: crypto.randomUUID(),
            taskListUnitId: input.taskListUnitId,
            taskListId: input.taskListId ?? null,
            name: input.name ?? '',
            description: input.description ?? '',
            scheduleRule: input.scheduleRule ?? null,
            relatedPage: input.relatedPage ?? null,
            config: input.config ?? null,
            images: input.images ?? null,
            isArchived: input.isArchived ?? false,
            // Corrective-task fields (reliability feature) — round-tripped so the
            // grid shows the corrective marker and the linked issue resolves.
            isCorrective: input.isCorrective ?? false,
            linkedIssueId: input.linkedIssueId ?? null,
            correctiveReason: input.correctiveReason ?? null,
            lateCloseEnabled: input.lateCloseEnabled ?? false,
            lateCloseUntilDays: input.lateCloseUntilDays ?? null,
            version: 1,
            createdAt: now,
            updatedAt: now,
            createdBy: me,
            updatedBy: me,
        };
        updateMockDb(db => {
            db.tasks.push(task);
            // A corrective task is created straight from the Issues triage drawer,
            // which does NOT separately update the unit. Append it to the chosen
            // list's childIds so it renders in the grid (mock-only convenience —
            // the normal add-task flow updates the unit itself via updateTaskListUnit).
            if (task.isCorrective && task.taskListId) {
                const unitIdx = db.taskListUnits.findIndex(u => u.id === task.taskListUnitId);
                if (unitIdx !== -1) {
                    const list = db.taskListUnits[unitIdx].taskLists?.[task.taskListId];
                    if (list && Array.isArray(list.childIds) && !list.childIds.includes(task.id)) {
                        list.childIds = [...list.childIds, task.id];
                    }
                }
            }
        });
        return task;
    },

    deleteTask: (variables: any) => {
        const input = variables?.input;
        if (!input?.id) return null;
        let removed: any = null;
        updateMockDb(db => {
            const idx = db.tasks.findIndex(t => t.id === input.id);
            if (idx !== -1) {
                [removed] = db.tasks.splice(idx, 1);
            }
        });
        return removed;
    },

    createTaskReport: (variables: any) => {
        const input = variables?.input;
        if (!input) return null;
        const now = new Date().toISOString();
        const me = getCurrentPersonaId();
        // The server derives the logical day from completedAt + the unit's timezone/shift.
        // Replicate it here so reports land in the correct calendar cell.
        const completedAt = input.completedAt ?? now;
        const logicalDay = getLogicalDay(
            new Date(completedAt),
            input.timezone ?? 'UTC',
            input.shiftHours ?? 4,
        );
        const report = {
            __typename: 'TaskReport' as const,
            id: crypto.randomUUID(),
            taskId: input.taskId,
            taskListUnitId: input.taskListUnitId,
            completedAt,
            logicalDayStart: logicalDay.toISOString(),
            status: input.status ?? 'completed',
            comment: input.comment ?? null,
            images: input.images ?? [],
            commentImages: input.commentImages ?? [],
            completedBy: input.completedBy ?? [me],
            version: 1,
            createdAt: now,
            updatedAt: now,
            createdBy: me,
            updatedBy: me,
        };
        updateMockDb(db => { db.taskReports.push(report); });

        // Server-owned: a "Can't Complete" report (status 'incomplete') auto-raises a
        // cant_complete Issue. Replicate it here so the demo matches production.
        if (report.status === 'incomplete') {
            const db = getMockDb();
            const unit = db.taskListUnits.find(u => u.id === input.taskListUnitId);
            const task = db.tasks.find(t => t.id === input.taskId);
            if (unit?.workspaceId) {
                const issue = newIssue({
                    workspaceId: unit.workspaceId,
                    source: 'cant_complete',
                    title: task?.name ?? 'Task',
                    description: report.comment,
                    images: report.images,
                    taskListUnitId: input.taskListUnitId,
                    taskId: input.taskId,
                    taskReportId: report.id,
                });
                updateMockDb(d => { d.issues.unshift(issue); });
            }
        }

        return report;
    },

    deleteTaskReport: (variables: any) => {
        const input = variables?.input;
        if (!input?.id) return null;
        let removed: any = null;
        updateMockDb(db => {
            const idx = db.taskReports.findIndex(r => r.id === input.id);
            if (idx !== -1) {
                [removed] = db.taskReports.splice(idx, 1);
            }
        });
        return removed;
    },

    deleteItemForever: (variables: any) => {
        const input = variables?.input;
        if (!input?.id) return null;
        let removed: Item | null = null;
        updateMockDb(db => {
            const idx = db.items.findIndex(i => i.id === input.id);
            if (idx !== -1) {
                [removed] = db.items.splice(idx, 1);
                // Remove from parent's childIds.
                const parentIds: string[] = JSON.parse(removed!.parentIds || '[]');
                for (const parentId of parentIds) {
                    const pIdx = db.items.findIndex(i => i.id === parentId);
                    if (pIdx !== -1) {
                        const childIds: string[] = JSON.parse(db.items[pIdx].childIds || '[]');
                        db.items[pIdx] = {...db.items[pIdx], childIds: JSON.stringify(childIds.filter(c => c !== input.id))};
                    }
                }
            }
        });
        return removed;
    },
};

// Mirrors prosemirror-server's TABLES_WITH_UPDATED_BY (versionedUpdate.ts) — the
// real server always stamps updated_at, and stamps updated_by only for these
// entities. The client never sends updatedAt/updatedBy (they're server-managed),
// so without this the mock silently kept whatever stale values the row was
// seeded with, e.g. showing "Last edited" equal to "Created" on any edit.
const COLLECTIONS_WITH_UPDATED_BY = new Set<keyof MockDb>(['items', 'taskListUnits', 'tasks', 'taskReports']);

function resolveMutation(field: string, variables: any): any {
    const collectionKey = MUTABLE_COLLECTIONS[field];
    const input = variables?.input;
    if (!collectionKey || !input) {
        return null;
    }

    const now = new Date().toISOString();
    let updated: any = null;
    updateMockDb((db) => {
        if (collectionKey === 'client') {
            db.client = {...db.client, ...input, updatedAt: now, version: (db.client.version ?? 0) + 1};
            updated = db.client;
            return;
        }
        const collection = db[collectionKey] as any[];
        const idx = collection.findIndex((row) => row.id === input.id);
        if (idx === -1) {
            return;
        }
        collection[idx] = {
            ...collection[idx],
            ...input,
            updatedAt: now,
            ...(COLLECTIONS_WITH_UPDATED_BY.has(collectionKey) ? {updatedBy: getCurrentPersonaId()} : {}),
            version: (collection[idx].version ?? 0) + 1,
        };
        updated = collection[idx];
    });
    return updated;
}

export function resolveMockOperation(field: string, variables: any): any {
    const resolver = resolvers[field];
    if (resolver) {
        return resolver(variables);
    }
    if (MUTABLE_COLLECTIONS[field]) {
        return resolveMutation(field, variables);
    }
    if (SILENT_EMPTY_LISTS.has(field)) {
        return listResult([]);
    }
    console.warn(`[mock-backend] no resolver for "${field}" — returning empty list fallback`);
    return listResult([]);
}
